/* ─────────────────────────────────────────────────────────────
   AskAgentWidget — Floating agent launcher (canvas-only cleanup).

   Simplified from the old text-mode popover to a single FAB that
   navigates to /agent. The ASK_OPEN_EVENT contract is preserved:
   contextual entry points (e.g. AskHelpLink) still dispatch it,
   and the widget responds by navigating to /agent.

   Returns null when not inside AuthProvider (AC: A43.9).
   ───────────────────────────────────────────────────────────── */

import { useEffect, useContext } from 'react'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { AuthContext } from '@/context/AuthContext'
import { AskFloatingButton } from './AskFloatingButton'
import { ASK_OPEN_EVENT } from './ask-trigger'

export function AskAgentWidget() {
  // Use useContext directly so we return null instead of throwing
  // when the widget is rendered outside the auth shell (AC: A43.9).
  const auth = useContext(AuthContext)
  if (!auth) return null

  return <AskAgentWidgetInner />
}

// Inner component is only rendered when auth is present.
/**
 * The three agency surfaces built to the reference designs (dashboard, Clients, Entities). The designs' guides name a floating AI
 * button over the data as an anti-pattern; those pages have their own Ask entry (the dashboard header) so the FAB is not drawn on them.
 * Everywhere else it is unchanged, and the ASK_OPEN_EVENT contract still works on every page.
 */
const FAB_HIDDEN_ON = new Set(['/', '/clients', '/entities'])

function AskAgentWidgetInner() {
  const navigate = useNavigate()
  const pathname = useLocation({ select: (l) => l.pathname.replace(/(.)\/+$/, '$1') })

  // Contextual entry points (e.g. AskHelpLink) open the agent by dispatching
  // ASK_OPEN_EVENT. Navigate to /agent instead of opening the old popover.
  useEffect(() => {
    const handler = () => { void navigate({ to: '/agent', search: { q: undefined } }) }
    window.addEventListener(ASK_OPEN_EVENT, handler)
    return () => window.removeEventListener(ASK_OPEN_EVENT, handler)
  }, [navigate])

  if (FAB_HIDDEN_ON.has(pathname)) return null

  return (
    <AskFloatingButton
      isOpen={false}
      onClick={() => void navigate({ to: '/agent', search: { q: undefined } })}
    />
  )
}
